/**
 * Utilidades del sistema de ayudas por pantalla.
 *
 * El contenido de una ayuda trae su propio CSS, escrito sin saber dónde se va a pintar. Para que no
 * se filtre al resto de la aplicación se le antepone un ámbito a cada selector antes de inyectarlo.
 */

var AMBITO_POR_DEFECTO = '.ayuda-render';

/**
 * At-rules con bloque cuyo cuerpo SÍ es una lista de reglas: se procesan por dentro para que sus
 * selectores queden acotados igual que los de fuera. Lo que no esté en la lista se DESCARTA.
 *
 * La lista iba antes al revés —se nombraban las que no anidan y se copiaba intacto todo lo demás—
 * y por ahí se colaban los nombres globales. `@keyframes loading` o `@font-face { font-family:
 * Roboto }` no pintan nada por sí solos, pero SUSTITUYEN la animación o la fuente de la aplicación
 * entera, que es justo lo que este módulo promete que no puede pasar. Con la lista al derecho, una
 * at-rule que CSS añada mañana deja de aplicarse dentro de la ayuda hasta que se añada aquí; con la
 * lista al revés, se aplicaba fuera. Que una ayuda pierda estilo se ve y se arregla; que pise el de
 * la aplicación, no.
 */
var AT_RULES_ANIDAN = /^@(-\w+-)?(media|supports|layer|container|scope|starting-style|document)\b/i;

/**
 * `@keyframes` es la excepción: también declara un nombre global, pero es el único de los globales
 * con uso legítimo dentro de una ayuda, así que en vez de descartarlo se le cambia el nombre por
 * uno propio del ámbito y se cambian igual las referencias que lo usan.
 *
 * El nombre tiene que ser un identificador limpio, y «limpio» quiere decir exactamente lo que
 * entiende `CAR_IDENT`, ni más ni menos: renombrar la declaración y renombrar las referencias son
 * dos lecturas del mismo nombre, y como no coincidan sale lo peor de los dos mundos —la declaración
 * cambia de nombre, la referencia no, y la animación se queda apuntando a algo que ya no existe—.
 * CSS admite además nombres entrecomillados y con escapes, y ahí no hay forma de renombrar con
 * seguridad: esos bloques se descartan, que es el lado bueno en el que equivocarse.
 */
// El arranque de un identificador CSS es: una letra (o lo que pase de ASCII), o un guion seguido de
// letra, o DOS guiones seguidos de cualquier cosa \u2014`--mio` es un nombre de animaci\u00F3n v\u00E1lido, igual
// que lo es de propiedad\u2014. Lo que sigue al arranque son los caracteres de `CAR_IDENT`.
var KEYFRAMES_CON_NOMBRE =
    /^(@(?:-\w+-)?keyframes)\s+((?:-?[A-Za-z_\u0080-\uFFFF]|--)[A-Za-z0-9_\-\u0080-\uFFFF]*)\s*$/i;

/**
 * Los caracteres que forman un identificador CSS. El acento de `aparición` es uno más: CSS admite
 * todo lo que pase de US-ASCII en un identificador, y dar por hecho que un nombre es ASCII partía
 * `aparición` en dos al buscar la referencia.
 */
var CAR_IDENT = /[A-Za-z0-9_\-\u0080-\uFFFF]/;

/** Declaraciones cuyo valor lleva el nombre de una animación. */
var PROP_ANIMACION = /^(-\w+-)?animation(-name)?$/i;

/**
 * At-rules sueltas (las que acaban en `;` en vez de abrir bloque) que se conservan.
 *
 * Aquí la lista también va al derecho, y por el mismo motivo: perseguir el nombre `@import` para
 * descartarlo es una carrera perdida —CSS deja escribirlo con escapes, `@\69mport` tokeniza igual—
 * mientras que una lista de lo que se acepta no tiene esa vuelta, porque lo que no se reconozca
 * letra a letra se descarta. Y las dos que se aceptan hacen falta de verdad: `@layer a, b;` fija el
 * orden de las capas, y sin ella el navegador lo deduce del orden de aparición, que puede ser el
 * contrario y hacer ganar a la regla que no debía; `@namespace` es lo que hace válido un selector
 * con prefijo (`svg|circle`). Ninguna de las dos sale del `<style>` que se inyecta.
 */
var AT_RULES_SUELTAS_QUE_SE_QUEDAN = /^@(layer|namespace)\b/i;

/** Un `:root`, `html` o `body` al principio del selector: dentro de una ayuda, el contenedor. */
var RAIZ = /^(:root|html|body)/i;

/**
 * Antepone `ambito` a todos los selectores de `css`.
 *
 * Los bloques anidados (@media, @scope y compañía) se procesan por dentro; los `@keyframes` salen
 * con otro nombre, para que no sustituyan una animación de la aplicación; y lo demás que declare un
 * nombre global o traiga una hoja de fuera (@font-face, @property, @import) se descarta.
 */
export function prefijarCss(css, ambito, animaciones) {
    if (!css) return '';
    ambito = ambito || AMBITO_POR_DEFECTO;
    var limpio = quitarComentarios(css);
    // Los nombres de animación se recogen ANTES de procesar nada: una animación se puede usar más
    // arriba de donde se declara, y si se renombrara sobre la marcha esas referencias se quedarían
    // apuntando al nombre viejo, que es justo el de la aplicación.
    return procesarBloques(limpio, ambito, animaciones || nombresDeKeyframes(limpio));
}

/**
 * Los nombres que `css` declara con `@keyframes`.
 *
 * Se exporta para poder acotar una ayuda repartida en varias hojas —el campo `css` y los `<style>`
 * que venga en el HTML—: si cada hoja se acotara por su cuenta, una animación declarada en una y
 * usada en otra se cambiaría de nombre en la declaración pero no en la referencia, y la ayuda
 * acabaría animándose con la animación que la aplicación tenga con ese nombre, si tiene alguna.
 */
export function animacionesDe(css) {
    return css ? nombresDeKeyframes(quitarComentarios(css)) : [];
}

/**
 * Traduce la ruta actual del router a los parámetros del endpoint /ayudas/resolver.
 *
 * Por defecto se identifica la pantalla por su ruta, recortada en el primer parámetro: la pantalla
 * es la misma para todos los ids, así que `/inspecciones/view/:id/` se pregunta como
 * `/inspecciones/view/`. Una ruta puede declarar `ayuda: 'clave'` en su definición de routes.js
 * para mandar sobre la ruta: así dos pantallas comparten ayuda, o una tiene la de otra.
 */
export function claveDesdeRuta(route) {
    if (!route) return {};
    var definicion = route.route || {};
    if (definicion.ayuda) return { clave: definicion.ayuda };
    var ruta = rutaSinParametros(definicion.path || route.path || route.url);
    return ruta ? { ruta: ruta } : {};
}

/**
 * Qué se perdería al abrir este HTML en el editor visual.
 *
 * El editor visual es Quill: no edita HTML, lo parsea a su propio modelo y lo vuelve a escribir, así
 * que todo lo que no entiende desaparece. Para una ayuda con estructura —clases, `data-`, secciones—
 * eso no es un retoque de formato, es cargarse los ganchos de los que tira su script. Se avisa antes
 * con lo que hay de verdad en el contenido, no en abstracto.
 *
 * @returns {string[]} lista vacía si el HTML es texto con formato y se puede editar sin miedo.
 */
export function riesgosDelEditorVisual(html) {
    if (!html) return [];
    var riesgos = [];
    var comprobaciones = [
        [/\sdata-[a-z0-9-]+\s*=/i, 'los atributos data- (los usa el script para encontrar sus elementos)'],
        [/\sclass\s*=/i, 'las clases CSS (sin ellas, los estilos de la ayuda no aplican)'],
        [/\sid\s*=/i, 'los identificadores id'],
        [/<\/?(section|nav|article|aside|header|footer|main|form|details|summary)\b/i, 'las etiquetas de estructura (section, nav, details…)'],
        [/<style\b/i, 'los bloques <style> embebidos'],
        [/<(input|select|textarea|button)\b/i, 'los controles de formulario (buscadores, botones…)'],
        [/\sstyle\s*=/i, 'parte de los estilos en línea']
    ];
    comprobaciones.forEach(function (par) {
        if (par[0].test(html)) riesgos.push(par[1]);
    });
    return riesgos;
}

/** Corta la ruta en el primer segmento dinámico (`:id`, `*`) y deja la barra final. */
export function rutaSinParametros(ruta) {
    if (!ruta) return ruta;
    var partes = ruta.split('/');
    var fijas = [];
    for (var i = 0; i < partes.length; i++) {
        var parte = partes[i];
        if (parte.charAt(0) === ':' || parte.charAt(0) === '*' || parte.indexOf('(') !== -1) break;
        fijas.push(parte);
    }
    var limpia = fijas.join('/');
    if (limpia === '') return '/';
    return limpia.charAt(limpia.length - 1) === '/' ? limpia : limpia + '/';
}

/**
 * Quita los comentarios respetando las cadenas: un `content: "/* ejemplo *​/"` no es un comentario,
 * y con una expresión regular global se le arrancaba el texto a la ayuda sin avisar.
 */
function quitarComentarios(css) {
    var salida = '';
    var comilla = null;
    var profundidad = 0;
    for (var i = 0; i < css.length; i++) {
        var c = css.charAt(i);
        if (c === '\\') {
            salida += c;
            if (i + 1 < css.length) salida += css.charAt(++i);
            continue;
        }
        if (comilla) {
            salida += c;
            if (c === comilla) comilla = null;
            continue;
        }
        if (c === '"' || c === "'") {
            comilla = c;
            salida += c;
            continue;
        }
        if (c === '/' && css.charAt(i + 1) === '*') {
            var fin = css.indexOf('*/', i + 2);
            i = fin === -1 ? css.length : fin + 1;
            continue;
        }
        if (c === '(' || c === '[') profundidad++;
        else if ((c === ')' || c === ']') && profundidad > 0) profundidad--;
        // `<!--` y `-->` los tira el parser de CSS sin mirarlos —son el resto del truco de esconder
        // la hoja de los navegadores de hace treinta años— y aquí también se tiran, porque envolver
        // en ellos era una forma de disfrazar una at-rule: `<!-- @import url(x); -->` no empieza por
        // `@` una vez troceado, así que la lista de las que se aceptan lo daba por CSS corriente y
        // lo dejaba pasar. El navegador, en cambio, sí veía el @import y se traía la hoja entera sin
        // acotar. Quitándolos aquí, lo que llegue abajo ya es lo que el navegador va a leer.
        //
        // Sólo cuentan como tal fuera de paréntesis. Dentro de un `url()` sin comillas esos mismos
        // caracteres son parte de la dirección —un SVG en data-uri puede llevarlos— y quitarlos de
        // ahí no protege de nada: cambia la imagen.
        if (profundidad === 0 && c === '<' && css.substr(i, 4) === '<!--') {
            i += 3;
            continue;
        }
        if (profundidad === 0 && c === '-' && css.substr(i, 3) === '-->') {
            i += 2;
            continue;
        }
        salida += c;
    }
    return salida;
}

function procesarBloques(css, ambito, animaciones) {
    var salida = '';
    var i = 0;
    while (i < css.length) {
        var apertura = buscarApertura(css, i);
        if (apertura === -1) {
            salida += atRulesSueltas(css.slice(i));
            break;
        }
        var cabecera = css.slice(i, apertura);
        // Las at-rules sin bloque (@import, @charset, @namespace, @layer a, b;) acaban en ';' y
        // caen en la misma cabecera que el selector siguiente. Sin separarlas, la cabecera entera
        // empieza por '@', se copia tal cual y el selector que la acompaña sale SIN acotar: un
        // "@import url(x); body {...}" repintaría la aplicación entera.
        var ultimoPuntoYComa = ultimoIndiceLibre(cabecera, ';');
        if (ultimoPuntoYComa !== -1) {
            salida += atRulesSueltas(cabecera.slice(0, ultimoPuntoYComa + 1));
            cabecera = cabecera.slice(ultimoPuntoYComa + 1);
        }
        cabecera = cabecera.trim();
        var cierre = buscarCierre(css, apertura);
        var cuerpo = css.slice(apertura + 1, cierre);
        if (cabecera.charAt(0) === '@') {
            salida += atRuleConBloque(cabecera, cuerpo, ambito, animaciones);
        } else {
            salida += prefijarSelector(cabecera, ambito)
                + '{' + renombrarAnimaciones(cuerpo, animaciones, ambito) + '}';
        }
        i = cierre + 1;
    }
    return salida;
}

/** Qué hacer con una at-rule que abre bloque: procesarla por dentro, renombrarla o descartarla. */
function atRuleConBloque(cabecera, cuerpo, ambito, animaciones) {
    if (AT_RULES_ANIDAN.test(cabecera)) {
        return cabecera + '{' + procesarBloques(cuerpo, ambito, animaciones) + '}';
    }
    var keyframes = cabecera.match(KEYFRAMES_CON_NOMBRE);
    // El cuerpo de un `@keyframes` va tal cual: son fotogramas (`from`, `50%`), no selectores, y
    // prefijarlos los invalidaría. Tampoco hace falta acotarlo, porque sólo se llega a él por el
    // nombre, que ya va cambiado.
    if (keyframes) {
        return keyframes[1] + ' ' + prefijoAnimacion(ambito) + keyframes[2] + '{' + cuerpo + '}';
    }
    return '';
}

/** Prefijo con el que se aíslan los nombres de animación de una ayuda. */
function prefijoAnimacion(ambito) {
    var base = ambito.replace(/[^a-zA-Z0-9_-]/g, '');
    return (base || 'ayuda') + '--';
}

/**
 * Los nombres declarados con `@keyframes` en el CSS de la ayuda.
 *
 * Recorre los bloques igual que `procesarBloques` en vez de buscar el nombre con una expresión
 * regular sobre el texto entero. La diferencia importa por dos motivos, y el segundo es el que
 * duele: una ayuda que EXPLIQUE CSS —y estas ayudas explican cosas— puede llevar un
 * `content: "@keyframes loading"` de ejemplo, y la expresión regular se lo tomaba por una
 * declaración de verdad; a partir de ahí, un `animation: loading` legítimo de esa misma ayuda se
 * renombraba a un nombre que nadie declara y la animación desaparecía sin decir nada.
 *
 * Recorriendo los bloques se recogen exactamente los nombres que luego se van a emitir: los mismos
 * que `atRuleConBloque` renombra, ni uno más. Lo que esté dentro de una cadena no es una cabecera y
 * no llega hasta aquí.
 */
function nombresDeKeyframes(css) {
    var nombres = [];
    recogerKeyframes(css, nombres);
    return nombres;
}

function recogerKeyframes(css, nombres) {
    var i = 0;
    while (i < css.length) {
        var apertura = buscarApertura(css, i);
        if (apertura === -1) return;
        var cabecera = css.slice(i, apertura);
        var ultimoPuntoYComa = ultimoIndiceLibre(cabecera, ';');
        if (ultimoPuntoYComa !== -1) cabecera = cabecera.slice(ultimoPuntoYComa + 1);
        cabecera = cabecera.trim();
        var cierre = buscarCierre(css, apertura);
        if (AT_RULES_ANIDAN.test(cabecera)) {
            recogerKeyframes(css.slice(apertura + 1, cierre), nombres);
        } else {
            var keyframes = cabecera.match(KEYFRAMES_CON_NOMBRE);
            if (keyframes && nombres.indexOf(keyframes[2]) === -1) nombres.push(keyframes[2]);
        }
        i = cierre + 1;
    }
}

/**
 * Cambia, en las declaraciones `animation`, los nombres que la propia ayuda declara con
 * `@keyframes`.
 *
 * Si alguna referencia se escapa —dentro de una regla anidada, por ejemplo— lo que pasa es que esa
 * animación no se ve, porque apunta a un nombre que ya no existe. Molesto, pero no se lleva por
 * delante la animación de la aplicación, que es lo que había que evitar.
 */
function renombrarAnimaciones(cuerpo, animaciones, ambito) {
    if (!animaciones.length || cuerpo.indexOf(':') === -1) return cuerpo;
    var prefijo = prefijoAnimacion(ambito);
    return trocearPorPuntoYComa(cuerpo).map(function (declaracion) {
        var dosPuntos = recorrer(declaracion, 0, function (c) { return c === ':'; });
        if (dosPuntos === -1) return declaracion;
        var propiedad = declaracion.slice(0, dosPuntos);
        if (!PROP_ANIMACION.test(propiedad.trim())) return declaracion;
        return propiedad + ':'
            + renombrarEnValor(declaracion.slice(dosPuntos + 1), animaciones, prefijo);
    }).join('');
}

/**
 * Sustituye los nombres de `animaciones` que aparezcan sueltos en el valor, respetando las cadenas:
 * un `content` que diga "spin" no es una referencia a la animación.
 *
 * Un identificador escrito con escapes no se toca: como mucho esa animación no se verá, que es
 * preferible a renombrar a ciegas algo que no se ha entendido.
 */
function renombrarEnValor(valor, animaciones, prefijo) {
    var salida = '';
    var token = '';
    var comilla = null;
    function cerrarToken() {
        var cerrado = animaciones.indexOf(token) === -1 ? token : prefijo + token;
        token = '';
        return cerrado;
    }
    for (var i = 0; i < valor.length; i++) {
        var c = valor.charAt(i);
        if (comilla) {
            salida += c;
            if (c === comilla) comilla = null;
            continue;
        }
        if (c === '\\') {
            salida += cerrarToken() + c;
            if (i + 1 < valor.length) salida += valor.charAt(++i);
            continue;
        }
        if (c === '"' || c === "'") {
            salida += cerrarToken();
            comilla = c;
            salida += c;
            continue;
        }
        if (CAR_IDENT.test(c)) {
            token += c;
            continue;
        }
        salida += cerrarToken() + c;
    }
    return salida + cerrarToken();
}

/**
 * Descarta las at-rules sueltas (las que acaban en `;` en vez de abrir bloque) que no estén en
 * `AT_RULES_SUELTAS_QUE_SE_QUEDAN`. El resto del texto pasa tal cual.
 */
function atRulesSueltas(texto) {
    if (texto.indexOf('@') === -1) return texto;
    return trocearPorPuntoYComa(texto).filter(function (trozo) {
        var limpio = trozo.trim();
        return limpio.charAt(0) !== '@' || AT_RULES_SUELTAS_QUE_SE_QUEDAN.test(limpio);
    }).join('');
}

/**
 * Trocea por los `;` de primer nivel dejando cada `;` con su trozo, para poder rehacer el texto
 * juntando los que se conservan.
 *
 * Tiene que ser consciente de comillas Y de paréntesis. Un `url()` sin comillas admite el `;`
 * dentro, así que partir a lo bruto por `;` corta el `@import url(tema.css?a=1;b=2);` por la mitad
 * y deja suelto un `b=2)` que ya no parece una at-rule: se queda como CSS corriente, malforma la
 * hoja y puede tumbar las reglas acotadas que vengan detrás.
 */
function trocearPorPuntoYComa(texto) {
    var trozos = [];
    var actual = '';
    var comilla = null;
    var profundidad = 0;
    for (var i = 0; i < texto.length; i++) {
        var c = texto.charAt(i);
        if (c === '\\') {
            actual += c;
            if (i + 1 < texto.length) actual += texto.charAt(++i);
            continue;
        }
        if (comilla) {
            actual += c;
            if (c === comilla) comilla = null;
            continue;
        }
        if (c === '"' || c === "'") {
            comilla = c;
            actual += c;
            continue;
        }
        if (c === '(' || c === '[') profundidad++;
        else if ((c === ')' || c === ']') && profundidad > 0) profundidad--;
        actual += c;
        if (c === ';' && profundidad === 0) {
            trozos.push(actual);
            actual = '';
        }
    }
    if (actual) trozos.push(actual);
    return trozos;
}

/** Primera `{` que no esté dentro de una cadena; -1 si no hay. */
function buscarApertura(css, desde) {
    return recorrer(css, desde, function (c) {
        return c === '{';
    });
}

/** Índice de la llave que cierra la abierta en `apertura`; el final de la cadena si no cierra. */
function buscarCierre(css, apertura) {
    var nivel = 0;
    var encontrado = recorrer(css, apertura, function (c) {
        if (c === '{') nivel++;
        else if (c === '}') {
            nivel--;
            return nivel === 0;
        }
        return false;
    });
    return encontrado === -1 ? css.length : encontrado;
}

/** Último `caracter` fuera de comillas dentro de `texto`; -1 si no hay. */
function ultimoIndiceLibre(texto, caracter) {
    var ultimo = -1;
    var desde = 0;
    while (true) {
        var i = recorrer(texto, desde, function (c) { return c === caracter; });
        if (i === -1) return ultimo;
        ultimo = i;
        desde = i + 1;
    }
}

/**
 * Recorre `texto` desde `desde` saltándose lo que va entre comillas y entre paréntesis, y devuelve
 * la primera posición donde `predicado` dice que sí. Sin esto, un `content: "{"` cuenta como llave
 * de verdad y descuadra el resto del CSS.
 *
 * Los paréntesis separan tanto como las comillas, y por el mismo motivo: lo que va dentro no es
 * estructura, es un valor. Un `@supports (background-image: url(data:image/png;base64,AAAA)) {…}`
 * lleva un `;` dentro del `url()` que no termina ninguna at-rule, y tomárselo por bueno partía la
 * cabecera en dos: el `@supports` se iba por el desagüe de las at-rules sueltas y el trozo que
 * quedaba —`base64,AAAA))`— se acotaba como si fuera un selector.
 */
function recorrer(texto, desde, predicado) {
    var comilla = null;
    var profundidad = 0;
    for (var i = desde; i < texto.length; i++) {
        var c = texto.charAt(i);
        // La barra invertida escapa al carácter siguiente TAMBIÉN fuera de las cadenas: `.a\"b` o
        // `.a\{b` son selectores válidos, y tomarse esa comilla o esa llave por buenas descuadra
        // todo lo que venga detrás.
        if (c === '\\') {
            i++;
            continue;
        }
        if (comilla) {
            if (c === comilla) comilla = null;
            continue;
        }
        if (c === '"' || c === "'") {
            comilla = c;
            continue;
        }
        if (c === '(' || c === '[') {
            profundidad++;
            continue;
        }
        if ((c === ')' || c === ']') && profundidad > 0) {
            profundidad--;
            continue;
        }
        if (profundidad === 0 && predicado(c)) return i;
    }
    return -1;
}

function prefijarSelector(cabecera, ambito) {
    return dividirSelectores(cabecera).map(function (selector) {
        selector = selector.trim();
        if (!selector) return '';

        // :root, html y body dentro de una ayuda significan "el contenedor de la ayuda", y hay que
        // quitarlos TODOS los que encabecen el selector, calificados o no, hasta que dejen de
        // aparecer: debajo del contenedor no hay ningún body al que agarrarse, así que un
        // `html body .aviso` traducido a medias queda en `.ayuda-render body .aviso`, que no casa
        // con nada y deja la ayuda sin estilos.
        //
        // Lo que califica a una raíz (`html.dark`) NO se puede fundir con el contenedor. Se probó y
        // no vale: el contenedor es un `<div class="ayuda-render">` fijo que nunca lleva la clase
        // del documento, así que `.ayuda-render.dark` no casa jamás y el modo oscuro se perdía
        // igual, sólo que de una forma más difícil de ver. La raíz calificada se queda DELANTE, que
        // es lo que de verdad es: un ancestro. `html.dark body .aviso` → `html.dark .ayuda-render
        // .aviso`, que sigue exigiendo el contenedor y por tanto no puede pintar fuera de la ayuda.
        var contexto = '';
        var combinador = '';
        var resto = selector;
        var recortado = false;
        var raiz;
        while ((raiz = partirRaizEncabezando(resto))) {
            if (raiz.calificadores) {
                contexto += (contexto ? ' ' : '') + raiz.raiz + raiz.calificadores;
            }
            combinador = raiz.combinador;
            resto = raiz.resto;
            recortado = true;
        }
        if (!recortado) return ambito + ' ' + selector;
        // El combinador de hijo sólo sobrevive entre el contenedor y lo que le sigue. Entre el
        // contexto y el contenedor tiene que ser descendencia: el contenedor cuelga de donde cuelgue
        // la pantalla, no es hijo directo de `html`.
        var contenedor = (contexto ? contexto + ' ' : '') + ambito;
        if (!resto) return contenedor;
        return contenedor + (combinador ? ' ' + combinador + ' ' : ' ') + resto;
    }).filter(function (selector) {
        return !!selector;
    }).join(',');
}

/**
 * Parte un selector que empiece por `:root`, `html` o `body` en cuatro: la raíz, lo que la
 * califica, el combinador que la separa de lo siguiente y el resto. Devuelve null si no empieza por
 * una raíz.
 *
 * Lo que va detrás de la raíz tiene que empezar por `.`, `#`, `[` o `:` para ser un calificador; si
 * no, el nombre del elemento era más largo —`body-x` es una etiqueta propia, no el body— y esto no
 * era una raíz.
 */
function partirRaizEncabezando(selector) {
    var raiz = selector.match(RAIZ);
    if (!raiz) return null;
    var i = raiz[0].length;
    var calificadores = '';
    var comilla = null;
    var profundidad = 0;
    while (i < selector.length) {
        var c = selector.charAt(i);
        if (comilla) {
            calificadores += c;
            if (c === comilla) comilla = null;
            i++;
            continue;
        }
        if (c === '\\') {
            calificadores += c;
            if (i + 1 < selector.length) calificadores += selector.charAt(i + 1);
            i += 2;
            continue;
        }
        if (c === '"' || c === "'") {
            comilla = c;
            calificadores += c;
            i++;
            continue;
        }
        if (c === '(' || c === '[') profundidad++;
        else if ((c === ')' || c === ']') && profundidad > 0) profundidad--;
        // Un espacio o un combinador de primer nivel cierran la raíz. Dentro de `:not(...)` o de
        // `[href="a b"]` no separan nada.
        if (profundidad === 0 && /[\s>+~]/.test(c)) break;
        calificadores += c;
        i++;
    }
    if (calificadores && '.#[:'.indexOf(calificadores.charAt(0)) === -1) return null;
    var separacion = selector.slice(i).match(/^\s*([>+~])?\s*/);
    return {
        raiz: raiz[0],
        calificadores: calificadores,
        combinador: separacion[1] || '',
        resto: selector.slice(i + separacion[0].length)
    };
}

/**
 * Parte una lista de selectores por las comas de primer nivel. Las que van dentro de comillas, de
 * paréntesis o de corchetes no separan nada: `:is(.a, .b)` y `[data-x="a,b"]` son un solo selector,
 * y cortarlos por ahí deja un CSS roto que además falla en silencio.
 */
function dividirSelectores(cabecera) {
    var partes = [];
    var actual = '';
    var comilla = null;
    var profundidad = 0;
    for (var i = 0; i < cabecera.length; i++) {
        var c = cabecera.charAt(i);
        // Un `.size\,compact` es una clase que se llama «size,compact»: esa coma no separa nada.
        if (c === '\\') {
            actual += c;
            if (i + 1 < cabecera.length) actual += cabecera.charAt(++i);
            continue;
        }
        if (comilla) {
            actual += c;
            if (c === comilla) comilla = null;
            continue;
        }
        if (c === '"' || c === "'") {
            comilla = c;
            actual += c;
            continue;
        }
        if (c === '(' || c === '[') profundidad++;
        else if (c === ')' || c === ']') profundidad--;
        if (c === ',' && profundidad <= 0) {
            partes.push(actual);
            actual = '';
            continue;
        }
        actual += c;
    }
    partes.push(actual);
    return partes;
}

export default {
    prefijarCss: prefijarCss,
    animacionesDe: animacionesDe,
    claveDesdeRuta: claveDesdeRuta,
    rutaSinParametros: rutaSinParametros,
    riesgosDelEditorVisual: riesgosDelEditorVisual
};
